import React from "react";
import ReadAloud from "../../components/ReadAloud/ReadAloud";
import "../pages.css";

const Contribution = ({ fontSize }) => {
  return (
    <div className="page">
      <h1 className="page-title">Contribution</h1>
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          New Part for Monoterpene Production with Higher Yield
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Terpene production has been a fairly popular project in the realm of
          iGEM, with the engineered organisms often being <i>E. coli</i> and <i>S.
          cerevisiae</i>, and with the desired products spanning linalool,
          limonene, and pinene. Traditionally, <i>E. coli</i> has produced higher
          yields, but yeast naturally produces a direct precursor to most
          monoterpenes, which makes it easier to engineer (Sarria et al., 2014).
          All previous projects concerning the production of monoterpenes in
          yeast have directly utilized geranyl diphosphate and a terpene
          synthase; however, in order to achieve a much higher yield, we
          introduced a second heterologous gene that codes for neryl diphosphate
          synthase, which parallels the action of farnesyl diphosphate synthase
          in producing a cis-isomer to geranyl diphosphate that can also be
          efficiently utilized by terpene synthases (Ignea et al., 2019). Since
          there is no competing downstream pathway that neryl diphosphate feeds
          into, this system has the potential for achieving much higher yield.
          The new part for neryl diphosphate synthase has been added to the
          Registry, and although we have not reached the quantification step to
          verify that we have achieved a higher yield, we hope that this may
          serve as a useful basis for future teams to build on.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Previous Parts Expressed in Different Organisms
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          <i>P. taeda</i> pinene synthase has been previously codon optimized for <i>E.
          coli</i> by UBC in 2011 (BBa_K517002); for our purposes, we have codon
          optimized it for expression in <i>S. cerevisiae</i>, and added it to the
          Parts registry (BBa_K4393001).
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Gibson Assembly Design Procedure
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Running a Gibson assembly reaction mix in itself is very simple, but
          the work required to reach this point is somewhat more involved. We
          include here a protocol for designing the gBlocks and primers, as well
          as a formula for the reaction mix.
        </div>
        <br></br>
        <h2
          style={{
            fontSize: `${26 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Synthesizing the gBlocks
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Each gBlock should include only the insert sequence to be inserted
          into the plasmid backbone, without any homology regions. In order to
          lower the manufacturing difficulties (i.e. high/low GC content, repeat
          regions, synthesis of a very long sequence, etc.), it is often helpful
          to break down a longer gene insert into multiple fragments or run the
          sequence through multiple codon optimizers. Note that it is much more
          convenient to increase the number of fragments in the design for
          Gibson assembly than for restriction digest, simply because this does
          not require any additional steps for added fragments.
        </div>
        <br></br>
        <h2
          style={{
            fontSize: `${26 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Designing the Primers
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          The primer sequences are where we add the homology regions for each
          insert; this is better than synthesizing the gBlocks with the homology
          regions added to them because this allows for more flexibility in
          redesigning. A very helpful tool is NEBuilder, where one can simply
          input all gBlocks, including the linearized plasmid, as well as
          parameters such as minimum homology region length and minimum
          annealing length; NEB will then provide suitable primers. Note that
          NEBuilder will provide a set of notes for every design, which include
          concerns that may cause problems during the actual synthesis or
          assembly; it is always a good idea to try and minimize these notes for
          a higher success rate.
        </div>
        <br></br>
        <h2
          style={{
            fontSize: `${26 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Assembling the Construct
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          First of all, the gBlocks should be amplified by PCR. For amplifying
          the plasmid backbone, due to its size, it is suggested to use a
          high-GC content buffer, such as the recipe provided in the Materials
          section. Additionally, for linearizing and adding homology regions to
          the plasmid backbone in one step, it is suggested to run a few cycles
          with the Tm of the annealing region of the primers first, and then run
          a few cycles with the Tm of the full length of the primers. Following
          amplification and confirmation with a gel, all amplified segments
          should be cleaned up and then added to a PCR tube alongside the Gibson
          assembly master mix, followed by incubation for a certain length of
          time. The NEB protocol card suggested an incubation time of 15 minutes
          for 2-3 fragments and 60 minutes for 4-6 fragments; however, we have
          found that an incubation of 60 minutes for 2-3 fragments and 80
          minutes or more for 4-6 fragments to be more effective. We have
          included <a href="https://docs.google.com/spreadsheets/d/1IaxG_gyZEhge81JtZgatpsIYPbtg-9tB/edit?usp=sharing&ouid=101554115410218114437&rtpof=true&sd=true">Gibson Assembly Recipe Calculator.xlsx</a> a spreadsheet
          that can be used to compute the amount of each reagent needed based on
          the length and concentration of each fragment.
        </div>
        <br></br>
        <h2
          style={{
            fontSize: `${26 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          Transformation
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Following assembly, we have found that running a gel is not very
          meaningful, as the concentration of the construct is generally too low
          for visualization. Usually, we proceed directly with 5 microlitres of
          the product mix to transformation into <i>E. coli</i> and plating on
          selective media to ensure assembly success.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
            marginBottom: "2vh",
          }}
        >
          References
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Ignea, C., Raadam, M. H., Motawia, M. S., Makris, A. M., Vickers, C.
          E., & Kampranis, S. C. (2019). Orthogonal monoterpenoid biosynthesis
          in yeast constructed on an isomeric substrate. <i>Nature
          Communications</i>, <i>10</i>(1). <a href="https://doi.org/10.1038/s41467-019-11290-x">https://doi.org/10.1038/s41467-019-11290-x</a>
        </div><br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Sarria, S., Wong, B., Martín, H. G., Keasling, J. D., & Peralta-Yahya,
          P. (2014). Microbial synthesis of pinene. <i>ACS Synthetic Biology</i>,
          <i>3</i>(7), 466–475. <a href="https://doi.org/10.1021/sb4001382">https://doi.org/10.1021/sb4001382</a>
        </div>
      </ReadAloud>
    </div>
  );
};

export default Contribution;
